import { captureException } from "@sentry/react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OutOfAreaActivationCloseReasonDrawer } from "~/components/common/outOfAreaActivationCloseReasonDrawer";
import { Button } from "~/components/ui/button";
import { useConfirmationDialog } from "~/components/ui/confirmationDialog";
import { DrawerProps } from "~/components/ui/drawer";
import { Heading } from "~/components/ui/heading";
import { AlertTriangleOutlineIcon } from "~/components/ui/icon";
import { Stack } from "~/components/ui/stack";
import { useToast } from "~/components/ui/toast";
import { tsr } from "~/utils/client";

type CloseButtonProps = {
  activationId: string;
};

export const CloseButton = ({ activationId }: CloseButtonProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  return (
    <>
      <Button variant="destructive" onClick={() => setIsDrawerOpen(true)}>
        Close Request
      </Button>
      <CloseReasonDrawer
        activationId={activationId}
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </>
  );
};

type CloseReasonDrawerProps = Omit<DrawerProps, "children"> & CloseButtonProps;

const CloseReasonDrawer = ({
  activationId,
  open,
  onClose,
}: CloseReasonDrawerProps) => {
  const navigate = useNavigate();
  const queryClient = tsr.useQueryClient();

  const { mutateAsync, isPending: isClosing } =
    tsr.outOfAreaActivations.close.useMutation();

  const { showConfirmationDialog } = useConfirmationDialog();
  const { addToast } = useToast();

  const handleClose = useCallback(
    async (reason: string) => {
      try {
        await mutateAsync({
          params: {
            id: activationId,
          },
          body: {
            reason,
          },
        });

        await queryClient.invalidateQueries({
          queryKey: ["out-of-area-activation-requests", "list"],
        });

        navigate("/requests?tab=out-of-area");
      } catch (error) {
        captureException(error);

        addToast({
          tone: "critical",
          title: "Sorry, something went wrong",
          message: "Please try again",
        });
      }
    },
    [activationId]
  );

  return (
    <OutOfAreaActivationCloseReasonDrawer
      busy={isClosing}
      open={open}
      onClose={onClose}
      onSubmit={reason =>
        showConfirmationDialog({
          title: (
            <Stack direction="row" align="center" gap="xsmall">
              <AlertTriangleOutlineIcon tone="critical" />
              <Heading level="3">Warning</Heading>
            </Stack>
          ),
          text: "You will close and archive this request for all members. This action cannot be undone.",
          onConfirm: () => handleClose(reason),
        })
      }
    />
  );
};
